import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, CheckCircle2, ChevronLeft, ChevronRight, Download, ChevronDown,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ONBOARDED_IMPL_REPORT } from "../../constants/api";
import dayjs from "dayjs";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_COLORS = {
  "In Progress": "bg-blue-100 text-blue-700",
  "Go Live":     "bg-green-100 text-green-700",
  "Completed":   "bg-emerald-100 text-emerald-700",
  "Suspended":   "bg-red-100 text-red-700",
};

const DATE_PRESETS = [
  { value: "all",        label: "All Time" },
  { value: "today",      label: "Today" },
  { value: "last_week",  label: "Last 7 Days" },
  { value: "last_month", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "this_year",  label: "This Year" },
  { value: "custom",     label: "Custom Range" },
];

const resolveRange = (preset, customFrom, customTo) => {
  const today = dayjs();
  const fmt = d => d.format("YYYY-MM-DD");
  switch (preset) {
    case "today":      return { from_date: fmt(today),                     to_date: fmt(today) };
    case "last_week":  return { from_date: fmt(today.subtract(7,  "day")), to_date: fmt(today) };
    case "last_month": return { from_date: fmt(today.subtract(30, "day")), to_date: fmt(today) };
    case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
    case "this_year":  return { from_date: fmt(today.startOf("year")),     to_date: fmt(today) };
    case "custom":     return (customFrom && customTo) ? { from_date: customFrom, to_date: customTo } : {};
    default:           return {};
  }
};

const Pagination = ({ total, page, pageSize, setPage, setPageSize }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  return (
    <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none">
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {`${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} of {total}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => setPage(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
          .map((p, i) => p === "…"
            ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
            : <button key={p} onClick={() => setPage(p)} className={`min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors ${p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>{p}</button>
          )
        }
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
      </div>
    </div>
  );
};

export default function ImplementationStatusReport() {
  const { fetch } = useApiMethods();
  const navigate  = useNavigate();

  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [tab,          setTab]          = useState("clients");
  const [datePreset,   setDatePreset]   = useState("all");
  const [customFrom,   setCustomFrom]   = useState("");
  const [customTo,     setCustomTo]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientPage,   setClientPage]   = useState(1);
  const [clientSize,   setClientSize]   = useState(25);
  const [delayedPage,  setDelayedPage]  = useState(1);
  const [delayedSize,  setDelayedSize]  = useState(25);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { from_date, to_date } = resolveRange(datePreset, customFrom, customTo);
      const params = new URLSearchParams();
      if (from_date)    params.append("from_date", from_date);
      if (to_date)      params.append("to_date",   to_date);
      if (statusFilter) params.append("status",    statusFilter);
      const qs = params.toString();
      const res = await fetch(`${ONBOARDED_IMPL_REPORT}${qs ? "?" + qs : ""}`);
      if (res?.status === "success") setData(res.data);
    } catch {
      ToastMsg("Failed to load implementation report", "error");
    } finally {
      setLoading(false);
    }
  }, [datePreset, customFrom, customTo, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setClientPage(1); setDelayedPage(1); }, [tab, datePreset, statusFilter]);

  const exportCSV = () => {
    if (!data) return;
    const rows = data.clients.map(c => [
      c.client_name, c.onboarding_status,
      fmtDate(c.onboarded_date), fmtDate(c.go_live_date),
      c.product_sold || "", c.assigned_to_name || "",
      c.total_milestones, c.done, `${c.percent}%`,
    ]);
    const csv = [
      ["Client","Status","Onboarded","Go Live","Product","Assigned To","Total MS","Done","%"],
      ...rows,
    ].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `implementation-report-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const clients  = data?.clients || [];
  const delayed  = data?.delayed_milestones || [];
  const completed  = clients.filter(c => c.onboarding_status === "Completed").length;
  const avgPercent = clients.length
    ? Math.round(clients.reduce((s, c) => s + c.percent, 0) / clients.length) : 0;

  const paginatedClients = clients.slice((clientPage - 1) * clientSize, clientPage * clientSize);
  const paginatedDelayed = delayed.slice((delayedPage - 1) * delayedSize, delayedPage * delayedSize);

  return (
    <ContainerCard>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Implementation Status Report</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Milestone completion rates and delayed items across all clients</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={loadData} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative">
          <select value={datePreset} onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); } }}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {datePreset === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
            <Button onClick={() => loadData()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
          </>
        )}
        <div className="relative">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none cursor-pointer">
            <option value="">All Statuses</option>
            <option value="In Progress">In Progress</option>
            <option value="Go Live">Go Live</option>
            <option value="Completed">Completed</option>
            <option value="Suspended">Suspended</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {(datePreset !== "all" || statusFilter) && (
          <button onClick={() => { setDatePreset("all"); setStatusFilter(""); setCustomFrom(""); setCustomTo(""); }}
            className="text-xs text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300">Clear</button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading…</p>
          </div>
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-4 mb-4 px-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">Total Clients: <span className="font-semibold text-gray-700 dark:text-gray-300">{clients.length}</span></span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Completed: <span className="font-semibold text-emerald-600">{completed}</span></span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Delayed Milestones: <span className="font-semibold text-red-600">{data?.total_delayed || 0}</span></span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Avg Completion: <span className="font-semibold text-gray-700 dark:text-gray-300">{avgPercent}%</span></span>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
            {[
              { id: "clients", label: `All Clients (${clients.length})` },
              { id: "delayed", label: `Delayed Milestones (${data?.total_delayed || 0})` },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                  tab === t.id ? "border-primary text-primary" : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab: All Clients */}
          {tab === "clients" && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400 gap-2">
                  <CheckCircle2 className="h-8 w-8 opacity-30" />
                  <p className="text-xs">No clients yet</p>
                </div>
              ) : (
                <>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Client</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Onboarded Date</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Go Live Date</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Product</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Milestones</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Progress</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {paginatedClients.map((c, idx) => (
                        <tr key={c.client_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/onboarded/${c.client_id}`)}>
                          <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(clientPage - 1) * clientSize + idx + 1}</td>
                          <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200">{c.client_name}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[c.onboarding_status] || "bg-gray-100 text-gray-600"}`}>
                              {c.onboarding_status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{fmtDate(c.onboarded_date)}</td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{fmtDate(c.go_live_date)}</td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[120px] truncate">{c.product_sold || "—"}</td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{c.assigned_to_name || "—"}</td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{c.done}/{c.total_milestones} done</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden w-24">
                                <div className={`h-full rounded-full ${c.percent === 100 ? "bg-emerald-500" : "bg-primary"}`}
                                  style={{ width: `${c.percent}%` }} />
                              </div>
                              <span className="text-gray-600 dark:text-gray-400 w-8">{c.percent}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination total={clients.length} page={clientPage} pageSize={clientSize} setPage={setClientPage} setPageSize={setClientSize} />
                </>
              )}
            </div>
          )}

          {/* Tab: Delayed Milestones */}
          {tab === "delayed" && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {delayed.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400 gap-2">
                  <CheckCircle2 className="h-8 w-8 opacity-30 text-green-500" />
                  <p className="text-xs">No delayed milestones — great work!</p>
                </div>
              ) : (
                <>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Client</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Milestone</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Due Date</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Days Overdue</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {paginatedDelayed.map((m, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/onboarded/${m.client_id}`)}>
                          <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(delayedPage - 1) * delayedSize + i + 1}</td>
                          <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200">{m.client_name}</td>
                          <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{m.milestone_name}</td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{fmtDate(m.due_date)}</td>
                          <td className="px-3 py-2.5">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                              {m.days_overdue}d overdue
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">
                              {m.status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{m.assigned_to_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination total={delayed.length} page={delayedPage} pageSize={delayedSize} setPage={setDelayedPage} setPageSize={setDelayedSize} />
                </>
              )}
            </div>
          )}
        </>
      )}
    </ContainerCard>
  );
}
